import { toAbsoluteUserPhotoUrl } from "../../../../../services/userPhotoServices";

const CARD_WIDTH = 1016;
const CARD_HEIGHT = 638;

const TEMPLATE_URL = "/img/badges/coach-card-bg.png";
const FALLBACK_PHOTO = "/img/user_avatar.jpg";

const BLUE = "#09008f";
const RED = "#ff0036";

const FONT_FAMILY =
  '"Arial Black", "Montserrat", "Satoshi", "Arial", sans-serif';

const normalizeFilePart = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const getUserPhoto = (user) =>
  user?.photoURL ||
  user?.photo_url ||
  user?.photo ||
  user?.avatar ||
  user?.image ||
  "";

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error("Image source manquante."));
      return;
    }

    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error(`Impossible de charger l'image : ${src}`));
    image.src = src;
  });

const imageUrlToDataUrl = async (url) => {
  if (!url) return null;

  if (
    url.startsWith("data:") ||
    url.startsWith("blob:") ||
    url.startsWith("/")
  ) {
    return url;
  }

  try {
    const response = await fetch(url, {
      mode: "cors",
      cache: "no-store",
    });

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();

    return await new Promise((resolve) => {
      const reader = new FileReader();

      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Photo non convertible en dataURL :", error);
    return null;
  }
};

const drawCoverImage = (ctx, image, x, y, width, height) => {
  const imageRatio = image.width / image.height;
  const boxRatio = width / height;

  let sourceWidth = image.width;
  let sourceHeight = image.height;
  let sourceX = 0;
  let sourceY = 0;

  if (imageRatio > boxRatio) {
    sourceWidth = image.height * boxRatio;
    sourceX = (image.width - sourceWidth) / 2;
  } else {
    sourceHeight = image.width / boxRatio;
    sourceY = (image.height - sourceHeight) / 2;
  }

  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    x,
    y,
    width,
    height,
  );
};

const fitFontSize = ({
  ctx,
  text,
  maxWidth,
  startSize,
  minSize,
}) => {
  let size = startSize;

  while (size > minSize) {
    ctx.font = `900 ${size}px ${FONT_FAMILY}`;

    if (ctx.measureText(text).width <= maxWidth) {
      return size;
    }

    size -= 2;
  }

  return minSize;
};

const drawNameLine = ({
  ctx,
  text,
  x,
  y,
  maxWidth,
  color,
  startSize = 52,
  minSize = 30,
}) => {
  const safeText = String(text || "").trim().toUpperCase();

  const fontSize = fitFontSize({
    ctx,
    text: safeText,
    maxWidth,
    startSize,
    minSize,
  });

  ctx.font = `900 ${fontSize}px ${FONT_FAMILY}`;
  ctx.fillStyle = color;
  ctx.textBaseline = "alphabetic";
  ctx.fillText(safeText, x, y);
};

const downloadCanvasAsPng = (canvas, filename) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Impossible de générer le PNG."));
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.setTimeout(() => URL.revokeObjectURL(url), 500);

        resolve();
      },
      "image/png",
      1,
    );
  });

export const generateCoachBadgePng = async (
  user,
  options = {},
) => {
  if (!user) {
    throw new Error("Utilisateur manquant.");
  }

  const templateUrl = options.templateUrl || TEMPLATE_URL;

  const prenom =
    String(user?.prenom || "").trim() ||
    String(user?.first_name || "").trim() ||
    "PRENOM";

  const nom =
    String(user?.nom || "").trim() ||
    String(user?.last_name || "").trim() ||
    "NOM";

  const canvas = document.createElement("canvas");

  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;

  const ctx = canvas.getContext("2d");

  const template = await loadImage(templateUrl);

  ctx.drawImage(template, 0, 0, CARD_WIDTH, CARD_HEIGHT);

  /**
   * Zone nom/prénom.
   * Le rectangle blanc permet aussi d'utiliser un template qui aurait
   * encore les textes PRENOM / NOM en exemple.
   */
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(72, 214, 430, 110);

  drawNameLine({
    ctx,
    text: prenom,
    x: 82,
    y: 263,
    maxWidth: 390,
    color: RED,
    startSize: 52,
    minSize: 30,
  });

  drawNameLine({
    ctx,
    text: nom,
    x: 82,
    y: 311,
    maxWidth: 390,
    color: BLUE,
    startSize: 52,
    minSize: 30,
  });

  /**
   * Zone photo.
   */
  const photoRawUrl = toAbsoluteUserPhotoUrl(getUserPhoto(user));
  const photoDataUrl = await imageUrlToDataUrl(photoRawUrl);
  const finalPhotoUrl = photoDataUrl || FALLBACK_PHOTO;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(694, 88, 268, 268);

  try {
    const photo = await loadImage(finalPhotoUrl);

    drawCoverImage(ctx, photo, 706, 100, 244, 244);

    ctx.strokeStyle = "rgba(9, 0, 143, 0.18)";
    ctx.lineWidth = 3;
    ctx.strokeRect(706, 100, 244, 244);
  } catch (error) {
    console.warn("Photo utilisateur non chargée :", error);

    ctx.fillStyle = "#f1f5f9";
    ctx.fillRect(706, 100, 244, 244);

    ctx.fillStyle = "#94a3b8";
    ctx.font = `900 32px ${FONT_FAMILY}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("PHOTO", 828, 222);

    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
  }

  const filename = [
    "carte-coach",
    normalizeFilePart(nom),
    normalizeFilePart(prenom),
  ]
    .filter(Boolean)
    .join("-")
    .concat(".png");

  await downloadCanvasAsPng(canvas, filename);
};